/**
 * BookPresenceBar (iter-47, iter-50 realtime) — presence chip row for
 * workspace-shared books. Talks WebSocket first (`/api/ws/presence/…`)
 * for instant updates; falls back to the REST heartbeat + poll from
 * iter-47 if the socket can't be established.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Users, Wifi, WifiOff } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function _relative(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, Math.round((now - then) / 1000));
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  } catch { return ""; }
}

function _initials(nickname) {
  if (!nickname) return "?";
  const words = nickname.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

function _colorFor(nickname) {
  const palette = [
    "bg-rose-100 text-rose-800 border-rose-300",
    "bg-amber-100 text-amber-800 border-amber-300",
    "bg-emerald-100 text-emerald-800 border-emerald-300",
    "bg-sky-100 text-sky-800 border-sky-300",
    "bg-violet-100 text-violet-800 border-violet-300",
    "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
  ];
  let hash = 0;
  for (const c of nickname || "") hash = (hash * 31 + c.charCodeAt(0)) >>> 0;
  return palette[hash % palette.length];
}

function _wsUrlFor(bookId) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  // Convert http(s)://host → ws(s)://host
  const wsBase = base.replace(/^http/i, "ws");
  return `${wsBase}/api/ws/presence/${bookId}`;
}

/**
 * useBookPresence (iter-50) — tries WebSockets first for instant peer
 * updates; falls back to the iter-47 REST heartbeat + poll if the
 * socket errors or closes unexpectedly. `markTyping()` is idempotent
 * in either transport.
 */
export function useBookPresence(bookId, isShared) {
  const { user } = useAuth();
  const [peers, setPeers] = useState([]);
  const [lastEditAt, setLastEditAt] = useState("");
  const [transport, setTransport] = useState("idle"); // "ws" | "poll" | "idle"
  const typingUntilRef = useRef(0);
  const lastImmediateBeatRef = useRef(0);
  const wsRef = useRef(null);
  const pollTimerRef = useRef(null);

  // ---- REST heartbeat fallback ------------------------------------
  const restHeartbeat = useCallback(async () => {
    if (!bookId || !isShared) return;
    const typing = Date.now() < typingUntilRef.current;
    try {
      await api.post(`/books/${bookId}/presence/heartbeat`, { typing });
      const { data } = await api.get(`/books/${bookId}/presence`);
      setPeers(data?.peers || []);
      setLastEditAt(data?.last_edit_at || "");
    } catch (err) { /* silent */ }
  }, [bookId, isShared]);

  const startPolling = useCallback(() => {
    if (pollTimerRef.current) return;
    setTransport("poll");
    restHeartbeat();
    pollTimerRef.current = setInterval(() => {
      if (document.visibilityState === "visible") restHeartbeat();
    }, 15000);
  }, [restHeartbeat]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // ---- WebSocket transport ----------------------------------------
  const openWs = useCallback(() => {
    if (!bookId || !isShared) return;
    try {
      const ws = new WebSocket(_wsUrlFor(bookId));
      wsRef.current = ws;
      ws.onopen = () => {
        setTransport("ws");
        stopPolling();
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "presence") {
            // Backend WS peers don't carry `is_me` — decorate here so
            // the UI logic can reuse the REST-shaped payload.
            const uid = user?.id;
            const decorated = (msg.peers || []).map((p) => ({
              ...p, is_me: p.user_id === uid,
            }));
            setPeers(decorated);
            if (msg.last_edit_at) setLastEditAt(msg.last_edit_at);
          }
        } catch { /* ignore malformed frames */ }
      };
      ws.onerror = () => { /* handled in onclose */ };
      ws.onclose = (ev) => {
        wsRef.current = null;
        if (!isShared) return;
        // Fall back to polling if WS shuts down for any reason. We
        // deliberately don't auto-reconnect the WS — the polling loop
        // is stable and cheap.
        startPolling();
      };
    } catch {
      startPolling();
    }
  }, [bookId, isShared, user?.id, startPolling, stopPolling]);

  useEffect(() => {
    if (!bookId || !isShared) return;
    openWs();
    return () => {
      stopPolling();
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* ignore */ }
        wsRef.current = null;
      }
    };
  }, [bookId, isShared, openWs, stopPolling]);

  // ---- Public API -------------------------------------------------
  const markTyping = useCallback(() => {
    typingUntilRef.current = Date.now() + 18000;
    if (!bookId || !isShared) return;
    const now = Date.now();
    // On WS: send immediately (no throttling — WS frames are cheap).
    // On polling: throttle to one beacon per 4 s.
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({ type: "typing", typing: true }));
      } catch { /* ignore */ }
      return;
    }
    if (now - lastImmediateBeatRef.current < 4000) return;
    lastImmediateBeatRef.current = now;
    api.post(`/books/${bookId}/presence/heartbeat`, { typing: true })
       .catch(() => { /* best-effort */ });
  }, [bookId, isShared]);

  return { peers, lastEditAt, markTyping, transport, userId: user?.id };
}

export function BookPresenceBar({ book, presence }) {
  if (!book?.workspace_id) return null;
  const { peers, lastEditAt, transport } = presence;
  const others = peers.filter((p) => !p.is_me);
  const typingOthers = others.filter((p) => p.is_typing);

  let statusLine;
  if (typingOthers.length === 1) {
    statusLine = <span><span className="font-semibold">{typingOthers[0].nickname}</span> is typing…</span>;
  } else if (typingOthers.length > 1) {
    statusLine = <span><span className="font-semibold">{typingOthers.length} people</span> are typing…</span>;
  } else if (others.length === 0) {
    statusLine = <span className="italic">You're the only one here right now.</span>;
  } else {
    const names = others.slice(0, 3).map((p) => p.nickname).join(", ");
    const extra = others.length > 3 ? ` +${others.length - 3}` : "";
    statusLine = <span>{names}{extra} viewing</span>;
  }

  const transportIcon = transport === "ws"
    ? <Wifi className="w-3 h-3 text-emerald-600" title="Realtime connection"/>
    : <WifiOff className="w-3 h-3 text-amber-600" title="Polling every 15 s (WebSocket unavailable)"/>;

  return (
    <div className="mt-4 border border-[color:var(--border)] bg-white px-3 py-2 flex items-center justify-between text-xs"
         data-testid="book-presence-bar">
      <div className="flex items-center gap-3 min-w-0">
        <Users className="w-3.5 h-3.5 text-[color:var(--text-muted)] shrink-0" />
        <div className="flex items-center -space-x-1 shrink-0" data-testid="book-presence-avatars">
          {peers.slice(0, 6).map((p) => (
            <span key={p.user_id}
                  title={`${p.nickname}${p.is_me ? " (you)" : ""}${p.is_typing ? " — typing" : ""}`}
                  className={"relative inline-flex items-center justify-center w-6 h-6 rounded-full border text-[10px] font-semibold " + _colorFor(p.nickname)}
                  data-testid={`book-presence-avatar-${p.user_id}`}>
              {_initials(p.nickname)}
              {p.is_typing && (
                <span className="absolute -bottom-0.5 -right-0.5 flex" aria-hidden="true">
                  <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-[color:var(--primary)] opacity-75"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[color:var(--primary)]"></span>
                </span>
              )}
            </span>
          ))}
        </div>
        <div className="text-[color:var(--text-muted)] truncate" data-testid="book-presence-status">
          {statusLine}
        </div>
      </div>
      <div className="flex items-center gap-2 whitespace-nowrap">
        <span data-testid="book-presence-transport">{transportIcon}</span>
        {lastEditAt && (
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]"
               data-testid="book-presence-last-edit">
            Last edit {_relative(lastEditAt)}
          </div>
        )}
      </div>
    </div>
  );
}
